import {
    ArrowRightIcon,
    ArrowsRightLeftIcon,
    CheckCircleIcon,
    CloudArrowUpIcon,
    MapIcon,
    PlusIcon,
    SparklesIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

import {
    MOCK_AI_INSIGHTS,
    MOCK_FAMILY,
    MOCK_PROVIDERS,
    MOCK_TEAM_SIGNALS,
    MockAiInsight,
    MockProvider,
    PROVIDER_TYPE_LABEL,
    ProviderStatus,
} from './mockData'

const STATUS_STYLES: Record<ProviderStatus, { label: string; cls: string }> = {
    connected: { label: 'Connected', cls: 'bg-green-100 text-green-800' },
    invited: { label: 'Invite sent', cls: 'bg-amber-100 text-amber-800' },
    manual: { label: 'Added manually', cls: 'bg-neutral-200 text-neutral-700' },
}

// Conflicts are only surfaced via the synthesis-hero card. Insights of kind
// 'conflict' are filtered out of the activity feed.
const HOME_INSIGHTS = MOCK_AI_INSIGHTS.filter((ins) => ins.kind !== 'conflict')

export default function CareTeamHome() {
    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8'>
                <div>
                    <p className='text-sm text-neutral-500'>Care team for</p>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>
                        {MOCK_FAMILY.childFirstName}, age {MOCK_FAMILY.childAge}
                    </h1>
                </div>
                <div className='flex items-center gap-2'>
                    <Link
                        to='/admin/family-care-team-preview/upload'
                        className={
                            'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium ' +
                            'text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50'
                        }
                    >
                        <CloudArrowUpIcon className='w-4 h-4' />
                        Upload document
                    </Link>
                    <Link
                        to='/admin/family-care-team-preview/team/add'
                        className={
                            'inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 ' +
                            'text-white text-sm font-medium rounded-lg transition-colors'
                        }
                    >
                        <PlusIcon className='w-4 h-4' />
                        Add a provider
                    </Link>
                </div>
            </div>

            {/* Hero synthesis card */}
            <div className='bg-gradient-to-br from-brand-600 to-brand-700 text-white rounded-2xl p-6 mb-6 shadow-sm'>
                <div className='flex items-start gap-3 mb-3'>
                    <div className='w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0'>
                        <SparklesIcon className='w-5 h-5 text-white' />
                    </div>
                    <div className='flex-1'>
                        <p className='text-xs uppercase tracking-wider text-white/70'>
                            BrightPath is keeping {MOCK_FAMILY.childFirstName}&apos;s roadmap in sync with
                        </p>
                        <div className='flex items-baseline gap-2 mt-1'>
                            <span className='text-3xl font-bold'>{MOCK_TEAM_SIGNALS.providersOnTeam}</span>
                            <span className='text-base text-white/80'>providers</span>
                            <span className='text-white/40 mx-1'>·</span>
                            <span className='text-3xl font-bold'>{MOCK_TEAM_SIGNALS.documentsUploaded}</span>
                            <span className='text-base text-white/80'>documents</span>
                            <span className='text-white/40 mx-1'>·</span>
                            <span className='text-3xl font-bold'>{MOCK_TEAM_SIGNALS.roadmapMilestones}</span>
                            <span className='text-base text-white/80'>milestones</span>
                        </div>
                    </div>
                </div>
                <div className='mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3'>
                    <Link
                        to='/admin/family-care-team-preview/roadmap'
                        className='flex items-center justify-between p-3 rounded-lg bg-white/10 hover:bg-white/15 transition-colors'
                    >
                        <span className='flex items-center gap-2 text-sm font-medium text-white'>
                            <MapIcon className='w-4 h-4' />
                            View {MOCK_FAMILY.childFirstName}&apos;s roadmap
                        </span>
                        <ArrowRightIcon className='w-4 h-4 text-white/70' />
                    </Link>
                    <Link
                        to='/admin/family-care-team-preview/conflicts'
                        className='flex items-center justify-between p-3 rounded-lg bg-white/10 hover:bg-white/15 transition-colors'
                    >
                        <span className='flex items-center gap-2 text-sm font-medium text-white'>
                            <ArrowsRightLeftIcon className='w-4 h-4' />
                            {MOCK_TEAM_SIGNALS.openConflicts} open conflict
                            {MOCK_TEAM_SIGNALS.openConflicts === 1 ? '' : 's'}
                        </span>
                        <ArrowRightIcon className='w-4 h-4 text-white/70' />
                    </Link>
                </div>
            </div>

            {/* Provider list */}
            <div className='bg-white rounded-2xl border border-neutral-200 p-6 mb-6'>
                <div className='flex items-center justify-between mb-4'>
                    <div className='flex items-center gap-2'>
                        <UserGroupIcon className='w-5 h-5 text-brand-600' />
                        <h2 className='text-lg font-semibold text-neutral-900'>Your providers</h2>
                    </div>
                    <Link
                        to='/admin/family-care-team-preview/team/add'
                        className='text-sm text-brand-600 hover:underline'
                    >
                        Add a provider
                    </Link>
                </div>
                <ul className='divide-y divide-neutral-100'>
                    {MOCK_PROVIDERS.map((p) => (
                        <ProviderRow key={p.id} provider={p} />
                    ))}
                </ul>
            </div>

            {/* AI insights feed */}
            <div className='bg-white rounded-2xl border border-neutral-200 p-6'>
                <div className='flex items-center justify-between mb-4'>
                    <div className='flex items-center gap-2'>
                        <SparklesIcon className='w-5 h-5 text-brand-600' />
                        <h2 className='text-lg font-semibold text-neutral-900'>What BrightPath has noticed</h2>
                    </div>
                </div>
                <ul className='space-y-3'>
                    {HOME_INSIGHTS.map((ins) => (
                        <InsightRow key={ins.id} insight={ins} />
                    ))}
                </ul>
            </div>
        </div>
    )
}

function ProviderRow({ provider }: { provider: MockProvider }) {
    const status = STATUS_STYLES[provider.status]
    return (
        <li className='py-3 flex items-center gap-3'>
            <div className='w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0'>
                <span className='text-xs font-semibold text-brand-700'>
                    {provider.clinicianName
                        .split(' ')
                        .filter((s) => /^[A-Z]/.test(s))
                        .slice(0, 2)
                        .map((s) => s[0])
                        .join('')}
                </span>
            </div>
            <div className='flex-1 min-w-0'>
                <div className='flex flex-wrap items-center gap-2'>
                    <Link
                        to={`/admin/family-care-team-preview/team/${provider.id}`}
                        className='text-sm font-medium text-neutral-900 hover:text-brand-700'
                    >
                        {provider.clinicianName}
                    </Link>
                    <span
                        className={
                            'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ' + status.cls
                        }
                    >
                        {status.label}
                    </span>
                </div>
                <div className='text-xs text-neutral-500'>
                    {PROVIDER_TYPE_LABEL[provider.type]} · {provider.organization}
                </div>
            </div>
            <div className='hidden sm:flex flex-col items-end text-xs text-neutral-500'>
                <span>
                    {provider.documentCount} document{provider.documentCount === 1 ? '' : 's'}
                </span>
                {provider.lastDocumentAt && <span>Last upload {provider.lastDocumentAt}</span>}
            </div>
            <Link
                to={`/admin/family-care-team-preview/team/${provider.id}`}
                className='text-neutral-400 hover:text-brand-600'
            >
                <ArrowRightIcon className='w-4 h-4' />
            </Link>
        </li>
    )
}

function InsightRow({ insight }: { insight: MockAiInsight }) {
    const kindIcon = (() => {
        switch (insight.kind) {
            case 'roadmap-update':
                return <MapIcon className='w-4 h-4 text-brand-600' />
            case 'cross-discipline':
                return <CheckCircleIcon className='w-4 h-4 text-green-600' />
            default:
                return <SparklesIcon className='w-4 h-4 text-brand-600' />
        }
    })()
    return (
        <li className='flex items-start gap-3 pb-3 border-b border-neutral-100 last:border-0 last:pb-0'>
            <div className='w-8 h-8 rounded-lg bg-neutral-50 flex items-center justify-center flex-shrink-0'>
                {kindIcon}
            </div>
            <div className='flex-1 min-w-0'>
                <div className='flex flex-wrap items-baseline justify-between gap-2'>
                    <span className='text-sm font-medium text-neutral-900'>{insight.headline}</span>
                    <span className='text-xs text-neutral-500'>{insight.at}</span>
                </div>
                <p className='text-xs text-neutral-600 mt-0.5'>{insight.detail}</p>
                {insight.linkPath && insight.linkLabel && (
                    <Link
                        to={insight.linkPath}
                        className='inline-flex items-center gap-1 mt-1 text-xs font-medium text-brand-700 hover:text-brand-800'
                    >
                        {insight.linkLabel}
                        <ArrowRightIcon className='w-3 h-3' />
                    </Link>
                )}
            </div>
        </li>
    )
}
